import { nonEmptyTrimmedStringFromUnknown } from "../protocol/shared.js";
import { clientMessageIdSchema } from "../protocol/client-message-id.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-diagnostic-report.ts
const diagnosticReportIdSchema = z$1.string().trim().min(1).max(128);
const diagnosticIdentifierSchema = z$1.string().trim().min(1).max(512);
const spaceDiagnosticReportProblemTypeSchema = z$1.enum([
	"message_problem",
	"no_reply",
	"reply_problem"
]);
const spaceDiagnosticReportWakePointerDerivationStatusSchema = z$1.enum([
	"exact",
	"source_multi_target",
	"fallback_multi_target",
	"not_wake",
	"unavailable"
]);
const spaceDiagnosticReportReasonCodeSchema = z$1.enum([
	"diagnostic_report.reply_source_signal_not_found",
	"diagnostic_report.reply_target_exact",
	"diagnostic_report.reply_target_missing_fallback",
	"diagnostic_report.terminal_failure_source_signal_not_found",
	"diagnostic_report.terminal_failure_target_exact",
	"diagnostic_report.source_wake_targets",
	"diagnostic_report.not_wake_related",
	"diagnostic_report.wake_pointer_unavailable"
]);
const createSpaceDiagnosticReportRouteParamsSchema = z$1.strictObject({ spaceId: nonEmptyTrimmedStringFromUnknown });
const createSpaceDiagnosticReportBodySchema = z$1.strictObject({
	reportedSignalId: diagnosticIdentifierSchema,
	problemType: spaceDiagnosticReportProblemTypeSchema
});
const spaceDiagnosticReportWakePointerSchema = z$1.strictObject({
	spaceId: diagnosticIdentifierSchema,
	clientMessageId: clientMessageIdSchema.nullable(),
	sourceSignalId: diagnosticIdentifierSchema,
	targetProfileId: diagnosticIdentifierSchema
});
const spaceDiagnosticReportWakePointerDerivationSchema = z$1.strictObject({
	status: spaceDiagnosticReportWakePointerDerivationStatusSchema,
	reasonCodes: z$1.array(spaceDiagnosticReportReasonCodeSchema).min(1).max(8)
});
const createSpaceDiagnosticReportResponseSchema = z$1.strictObject({
	diagnosticReportId: diagnosticReportIdSchema,
	spaceId: diagnosticIdentifierSchema,
	reportedSignalId: diagnosticIdentifierSchema,
	replyToSignalId: diagnosticIdentifierSchema.nullable(),
	threadId: diagnosticIdentifierSchema.nullable(),
	problemType: spaceDiagnosticReportProblemTypeSchema,
	reporterProfileId: diagnosticIdentifierSchema,
	createdAtMs: z$1.int().min(0),
	wakePointers: z$1.array(spaceDiagnosticReportWakePointerSchema).max(64),
	wakePointerDerivation: spaceDiagnosticReportWakePointerDerivationSchema
}).superRefine((value, context) => {
	for (const [pointerIndex, pointer] of value.wakePointers.entries()) {
		if (pointer.spaceId === value.spaceId) continue;
		context.addIssue({
			code: "custom",
			message: "wake pointer spaceId must match report spaceId",
			path: [
				"wakePointers",
				pointerIndex,
				"spaceId"
			]
		});
	}
	const pointerCount = value.wakePointers.length;
	switch (value.wakePointerDerivation.status) {
		case "exact":
			if (pointerCount !== 1) context.addIssue({
				code: "custom",
				message: "exact diagnostic report must include one wake pointer",
				path: ["wakePointers"]
			});
			break;
		case "source_multi_target":
		case "fallback_multi_target":
			if (pointerCount < 1) context.addIssue({
				code: "custom",
				message: "multi-target diagnostic report must include at least one wake pointer",
				path: ["wakePointers"]
			});
			break;
		case "not_wake":
		case "unavailable":
			if (pointerCount !== 0) context.addIssue({
				code: "custom",
				message: "diagnostic report without wake evidence must not include wake pointers",
				path: ["wakePointers"]
			});
			break;
		default: return value.wakePointerDerivation.status;
	}
});
//#endregion
export { createSpaceDiagnosticReportBodySchema, createSpaceDiagnosticReportResponseSchema, createSpaceDiagnosticReportRouteParamsSchema, spaceDiagnosticReportProblemTypeSchema, spaceDiagnosticReportReasonCodeSchema, spaceDiagnosticReportWakePointerDerivationSchema, spaceDiagnosticReportWakePointerDerivationStatusSchema, spaceDiagnosticReportWakePointerSchema };

//# sourceMappingURL=space-diagnostic-report.js.map